/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
    getDocs,
    Firestore,
    writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Objectif } from "./objectif.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ObjectifStudent } from "../objectifStudent/objectifStudent.type";



export const ObjectifApi = createApi({
    reducerPath: "objectifApi",
    tagTypes: ["objectif", "objectifs", 'objectifsByStudent'],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditObjectif: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {

                        const students: string[] = data.students ?? [];
                        const { students: _, ...dataWithoutStudents } = data;

                        // Cas 1 : pas d'étudiant → création simple
                        if (students.length === 0) {
                            const docRef = await addDoc(collection(db, "objectifs"), {
                                ...data,
                                createdAt: serverTimestamp(),
                                createdBy: userId,
                                updatedAt: serverTimestamp(),
                            });

                            return {
                                data: {
                                    id: docRef.id,
                                    message: "Objectif créé !",
                                    ...data,
                                },
                            };
                        }

                        // Cas 2 : avec étudiants → créer l'objectif puis les objectifStudents
                        const docRef = await addDoc(collection(db, "objectifs"), {
                            ...data,
                            createdAt: serverTimestamp(),
                            createdBy: userId,
                            updatedAt: serverTimestamp(),
                        });

                        const docRefs = await Promise.all(
                            students.map((studentId: string) =>
                                addDoc(collection(db, "objectifStudents"), {
                                    studentId,
                                    objectifId: docRef.id,
                                    matiereId: dataWithoutStudents.matiereId,
                                    sousObjectifs: dataWithoutStudents.sousObjectifs || [],
                                    status: dataWithoutStudents.status || "Nouveau",
                                    createdAt: serverTimestamp(),
                                    createdBy: userId,
                                    updatedAt: serverTimestamp(),
                                })
                            )
                        );

                        return {
                            data: {
                                ids: docRefs.map((ref) => ref.id),
                                message: "Objectif créé !",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const students: string[] = data.students ?? [];
                        const { students: _, ...dataWithoutStudents } = data;

                        // 1. Mettre à jour l'objectif
                        const allDone = data.sousObjectifs?.every((s: any) => s.done === true);

                        const docRef = doc(db, "objectifs", id);
                        await updateDoc(docRef, {
                            ...data,
                            ...(allDone ? { status: "Terminé" } : { status: "En cours" }),
                            updatedAt: serverTimestamp(),
                        });

                        // 2. Récupérer les objectifStudents existants pour cet objectif
                        const osQuery = query(
                            collection(db, "objectifStudents"),
                            where("objectifId", "==", id)
                        );
                        const osSnapshot = await getDocs(osQuery);

                        // IDs des étudiants déjà liés à cet objectif
                        const existingStudentIds = osSnapshot.docs.map(
                            (d) => d.data().studentId as string
                        );

                        // 3. Ajouter les nouveaux étudiants qui n'existent pas encore
                        const studentsToAdd = students.filter(
                            (studentId) => !existingStudentIds.includes(studentId)
                        );

                        // 4. Supprimer les étudiants qui ont été retirés
                        const studentsToRemove = osSnapshot.docs.filter((d) =>
                            !students.includes(d.data().studentId)
                        );

                        await Promise.all([
                            // Ajout des nouveaux
                            ...studentsToAdd.map((studentId) =>
                                addDoc(collection(db, "objectifStudents"), {
                                    studentId,
                                    objectifId: id,
                                    matiereId: dataWithoutStudents.matiereId,
                                    sousObjectifs: dataWithoutStudents.sousObjectifs || [],
                                    status: "Nouveau",
                                    createdAt: serverTimestamp(),
                                    createdBy: userId,
                                    updatedAt: serverTimestamp(),
                                })
                            ),
                            // Suppression des retirés
                            ...studentsToRemove.map((d) =>
                                deleteDoc(doc(db, "objectifStudents", d.id))
                            ),
                        ]);


                        return {
                            data: {
                                message:
                                    "Objectif modifié !",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["objectif", "objectifs"],
        }),
        getObjectifs: builder.query({
            async queryFn() {
                try {
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "objectifs"),
                            orderBy("createdAt", "desc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const Objectifs = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: Objectifs as Objectif[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const ObjectifsRef = query(
                    collection(db, "objectifs"),
                    orderBy("createdAt", "desc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(ObjectifsRef, (snapshot) => {
                        const Objectifs = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => Objectifs as Objectif[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["objectifs"],
        }),
        getObjectifsByProf: builder.query<Objectif[], void>({
            async queryFn() {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }

                    const q = query(
                        collection(db, "objectifs"),
                        where("createdBy", "==", user.uid),
                        orderBy("createdAt", "desc")
                    );

                    const snapshot = await getDocs(q);

                    const objectifs = await Promise.all(
                        snapshot.docs.map(async (objectifDoc) => {
                            const data = objectifDoc.data();

                            // Récupérer la matière
                            let matiereData = null;
                            if (data.matiereId) {
                                const matiereSnap = await getDoc(doc(db, "matieres", data.matiereId));
                                if (matiereSnap.exists()) {
                                    matiereData = { id: matiereSnap.id, ...matiereSnap.data() };
                                } else {
                                    matiereData = data.matiereId;
                                }
                            }

                            // Récupérer tous les students
                            let studentsData: any[] = [];

                            if (data.students && data.students.length > 0) {
                                const studentsPromises = data.students.map(async (studentId: string) => {
                                    const studentSnap = await getDoc(
                                        doc(db, "users", studentId)
                                    );
                                    if (studentSnap.exists()) {
                                        return {
                                            id: studentSnap.id,
                                            ...studentSnap.data(),
                                        };
                                    }
                                    return null;
                                });

                                studentsData = (await Promise.all(studentsPromises)).filter(Boolean);
                            }

                            return {
                                id: objectifDoc.id,
                                ...data,
                                matiereData,
                                studentsData,
                            } as Objectif;
                        })
                    );

                    return { data: objectifs };

                } catch (error) {
                    return { error };
                }
            },

            keepUnusedDataFor: 0,

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const user = auth.currentUser;
                if (!user) return;

                const objectifsRef = query(
                    collection(db, "objectifs"),
                    where("createdBy", "==", user.uid),
                    orderBy("createdAt", "desc")
                );

                let unsubscribe: (() => void) | undefined;

                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(objectifsRef, async (snapshot) => {
                        const objectifs = await Promise.all(
                            snapshot.docs.map(async (objectifDoc) => {
                                const data = objectifDoc.data();

                                // Récupérer la matière
                                let matiereData = null;
                                if (data.matiereId) {
                                    const matiereSnap = await getDoc(doc(db, "matieres", data.matiereId));
                                    if (matiereSnap.exists()) {
                                        matiereData = { id: matiereSnap.id, ...matiereSnap.data() };
                                    } else {
                                        matiereData = data.matiereId;
                                    }
                                }

                                // Récupérer tous les students
                                let studentsData: any[] = [];

                                if (data.students && data.students.length > 0) {
                                    const studentsPromises = data.students.map(async (studentId: string) => {
                                        const studentSnap = await getDoc(
                                            doc(db, "users", studentId)
                                        );
                                        if (studentSnap.exists()) {
                                            return {
                                                id: studentSnap.id,
                                                ...studentSnap.data(),
                                            };
                                        }
                                        return null;
                                    });

                                    studentsData = (await Promise.all(studentsPromises)).filter(Boolean);
                                }

                                return {
                                    id: objectifDoc.id,
                                    ...data,
                                    matiereData,
                                    studentsData,
                                } as Objectif;
                            })
                        );

                        updateCachedData(() => objectifs);
                    });

                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },

            providesTags: ["objectifs"],
        }),

        getObjectifById: builder.query({
            queryFn: async (ObjectifId) => {
                try {
                    const ObjectifRef = doc(db, "objectifs", ObjectifId);
                    const ObjectifSnap = await getDoc(ObjectifRef);

                    if (!ObjectifSnap.exists()) {
                        return {
                            error: { status: 404, message: "Objectif introuvable !" },
                        };
                    }

                    const data = ObjectifSnap.data();

                    // 1. Fetch objectifStudents + students en parallèle
                    const [objectifStudentsSnap, studentMap]: [any, Record<string, any>] = await Promise.all([
                        getDocs(query(
                            collection(db, "objectifStudents"),
                            where("objectifId", "==", ObjectifId)
                        )),
                        data.students?.length
                            ? batchFetchDocs(db, "users", data.students)
                            : Promise.resolve({} as Record<string, any>),
                    ]);

                    // 2. Collecter les classeIds uniques des students
                    const classeIds = Array.from(new Set(
                        Object.values(studentMap)
                            .map((s: any) => s.classe)
                            .filter(Boolean)
                    )) as string[];

                    // 3. Fetch classes
                    const classeMap: Record<string, any> = classeIds.length
                        ? await batchFetchDocs(db, "classrooms", classeIds)
                        : {};

                    // 4. Helper pour enrichir un student avec sa classe
                    const enrichStudent = (studentId: string) => {
                        const student = studentMap[studentId];
                        if (!student) return null;
                        return {
                            ...student,
                            classeData: student.classe
                                ? (classeMap[student.classe] ?? null)
                                : null,
                        };
                    };

                    // 5. Assembler studentsData
                    const studentsData = (data.students ?? [])
                        .map((id: string) => enrichStudent(id))
                        .filter(Boolean);

                    // 6. Assembler objectifStudents
                    const objectifStudents = objectifStudentsSnap.docs.map((d: any) => ({
                        id: d.id,
                        ...d.data(),
                    }));

                    return {
                        data: {
                            id: ObjectifSnap.id,
                            ...data,
                            studentsData,
                            objectifStudents,
                        }
                    };

                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["objectif"],
        }),


        // Supprimer
        deleteObjectif: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const batch = writeBatch(db);

                    // 1. Récupérer les objectifStudents liés
                    const osSnapshot = await getDocs(
                        query(
                            collection(db, "objectifStudents"),
                            where("objectifId", "==", uid)
                        )
                    );

                    // 2. Vérifier la limite Firestore (500 ops max par batch)
                    if (osSnapshot.size >= 499) {
                        return {
                            error: {
                                status: "DELETE_ERROR",
                                message: "Trop d'entrées liées pour une suppression atomique.",
                            },
                        };
                    }

                    // 3. Ajouter les suppressions au batch
                    osSnapshot.docs.forEach((d) =>
                        batch.delete(doc(db, "objectifStudents", d.id))
                    );
                    batch.delete(doc(db, "objectifs", uid));

                    // 4. Exécuter tout en une seule transaction atomique
                    await batch.commit();

                    return {
                        data: { uid, message: "Objectif supprimé !", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["objectif", "objectifs"],
        }),
    }),
});

export const {
    useAddOrEditObjectifMutation,
    useGetObjectifsQuery,
    useGetObjectifsByProfQuery,
    useDeleteObjectifMutation,
    useLazyGetObjectifByIdQuery,
} = ObjectifApi;

export function useObjectifFromLocation(): [
    Objectif,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<Objectif | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetObjectifByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        // console.log("is id", id)
        if (id && id !== undefined) {
            // console.log("id", id)
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as Objectif);
        }
    }, [data]);

    return [item as Objectif & { bio: string }, isLoading, id as string, findById];
}


// ─── Helper : Batch fetch avec chunks de 30 (limite Firestore "in") ──────────

async function batchFetchDocs(
    db: Firestore,
    collectionName: string,
    ids: string[]
): Promise<Record<string, any>> {
    if (ids.length === 0) return {};

    const CHUNK_SIZE = 30; // limite Firestore pour l'opérateur "in"
    const chunks: string[][] = [];

    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        chunks.push(ids?.slice(i, i + CHUNK_SIZE));
    }

    const results = await Promise.all(
        chunks.map(chunk =>
            getDocs(query(
                collection(db, collectionName),
                where("__name__", "in", chunk)
            ))
        )
    );

    const map: Record<string, any> = {};
    results.forEach(snapshot => {
        snapshot.docs.forEach(d => {
            map[d.id] = { id: d.id, ...d.data() };
        });
    });

    return map;
}